import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NgControl, NgModel, ValidationErrors} from '@angular/forms';

@Component({
  selector: 'ds-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss']
})
export class InputComponent implements ControlValueAccessor, AfterViewInit {

  @ViewChild('innerInput', {static: true}) innerInput: ElementRef;
  @Input() fieldName = '';
  @Input() label = '';
  @Input() placeholder = '';
  @Input() placeholderRemark = '';
  @Input() type = '';
  @Input() iconName: string;
  @Input() showClearButton = false;
  /** Object that contains an error message and an Icon class for error */
  @Input() error = {message: '', iconClass: ''};
  /** When showErrorMessages is set to false, showError() will always return false */
  @Input() showErrorMessages = true;
  @Input() tooltipPlacement = 'auto';
  @Input() tooltipContent: string | TemplateRef<any> = '';
  @Input() tooltipTitle: string;
  @Input() tooltipAriaLabel: string;
  @Input() hidePlaceholderWhenFilled = true;
  @Input() hidePlaceholderWhenFocused = true;
  @Output() focusEvent = new EventEmitter<boolean>();
  @Output() blurEvent = new EventEmitter<string>();
  public isDisabled = false;
  hasFocus = false;
  innerValue = '';
  private onTouchedCallback: () => {};
  private onChangeCallback: (_: any) => {};
  formControl: AbstractControl;

  /**
   * This component needs a value accessor reference to validate the input with angular validators.
   * Value accessor is not set using the providers to avoid running into a circular import.
   *
   * Further information:
   * https://material.angular.io/guide/creating-a-custom-form-field-control
   * https://stackoverflow.com/questions/44201298
   * https://stackoverflow.com/questions/57186593
   *
   * Please note that for when you use ngModel max and min validators a custom validator should be implemented.
   *
   * @param ngControl used when FormControl is applied to this input
   * @param ngModel used when ngModel is applied to this input
   * @param cdRef change detector reference
   */
  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() @Self() public ngModel: NgModel,
    private cdRef: ChangeDetectorRef
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
    if (ngModel) {
      ngModel.valueAccessor = this;
    }
  }

  ngAfterViewInit() {
    this.setFormControl(this.ngControl, this.ngModel);
    this.cdRef.detectChanges();
  }

  /**
   * Setting the value accessor directly (instead of using the providers)
   * to avoid running into a circular import.
   *
   * @param ngControl used when formcontrol is applied to this input
   * @param ngModel used when ngModel is applied to this input
   */
  setFormControl(ngControl: NgControl, ngModel: NgModel) {
    if (ngModel && ngModel.control) {
      this.formControl = ngModel.control;
    } else if (ngControl && ngControl.control) {
      this.formControl = ngControl.control;
    }
  }

  get value(): any {
    return this.innerValue;
  }

  /**
   * set accessor including call the onchange callback
   */
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      if (this.onChangeCallback) {
        this.onChangeCallback(v);
      }
    }
  }

  get errors(): ValidationErrors | null {
    return this.formControl ? this.formControl.errors : null;
  }

  /**
   * Decides if this input field is valid according to validation attributes.
   *
   * @returns false if the content of this input is valid otherwise true.
   */
  get invalid(): boolean {
    return this.formControl ? this.formControl.invalid : false;
  }

  /**
   * Decides if an error message should be shown for this input field.
   *
   * @returns true if an error message should be shown.
   */
  get showError(): boolean {
    if (this.showErrorMessages) {
      return this.invalid;
    }
    return false;
  }

  clearInput() {
    this.innerInput.nativeElement.value = '';
    this.innerInput.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onBlur() {
    this.blurEvent.emit(this.value);
  }

  /**
   * Prevents the ExpressionChangedAfterItHasBeenCheckedError when an input field is shown in a modal.
   *
   * @param isFocused new value of the hasFocus parameter
   */
  setHasFocus(isFocused: boolean = true) {
    this.hasFocus = isFocused;
    this.focusEvent.emit(isFocused);
  }

  setFocusState(isFocused: boolean = true) {
    if (isFocused) {
      // makes the dom render before this method is executed.
      setTimeout(() => {
        this.innerInput.nativeElement.focus();
      }, 0);
    } else {
      this.innerInput.nativeElement.blur();
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Reacts on [(ngModel)] changes.
   *
   * @param value value passed into [(ngModel)]
   */
  writeValue(value: any) {
    this.value = value;
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  /**
   * To be triggered when the value of this input element changes.
   */
  public onChange() {
    if (this.onChangeCallback) {
      this.onChangeCallback(this.innerValue);
    }
  }
}
